import { useInfoPageStore } from '~/modules/infoPages/stores/infoPageStore';
import { INFO_PAGES_CONFIG } from '~/modules/infoPages/config';

export const useInfoPages = () => {
  const { $api } = useNuxtApp() as NuxtAppType;
  const infoPagesStore = useInfoPageStore();

  /**
   * Получает список постов по заданным параметрам.
   * Использует запрос `GET v1/posts/`.
   *
   * @param {Object} payload - Данные для запроса постов
   * @param {number} payload.section - ID секции
   * @param {string[]} [payload.categories] - Фильтр по категориям (необязательный)
   * @param {number} payload.count - Количество постов на странице
   * @param {number} payload.page - Номер страницы
   * @throws {Error} Если запрос неуспешен
   * @returns {Promise<IResponse<IApiGetPostsResponse>>} Ответ API с постами
   */
  const getSectionPosts = async (payload: { section_id: number; categories?: string[]; count: number; page: number; includeHidden?: boolean }) => {
    const params = {
      ...payload,
      section_id: payload.section_id,
    };

    const response = await $api.infoPages.getPosts(params);
    if (!response.success) {
      throw new Error(response.message);
    }

    infoPagesStore.sectionsData[payload.section_id] = {
      items: response.data.items,
      count: response.data.count || 0,
    };

    return response;
  };

  /**
   * Получает детальную информацию о посте по полному пути.
   * Использует запрос `GET v1/posts/`.
   *
   * @param {Object} payload - Данные для запроса
   * @param {string} payload.fullPath - Полный путь поста
   * @throws {Error} Если запрос неуспешен или пост не найден
   * @returns {Promise<void>}
   */
  const getDetailPostByFullPath = async (payload: { fullPath: string; includeHidden?: boolean }) => {
    const getPostsRes = await $api.infoPages.getPosts({ fullPaths: [payload.fullPath], count: 1, page: 1, includeHidden: payload.includeHidden });
    if (!getPostsRes.success) {
      throw new Error(getPostsRes.message);
    }

    const post = getPostsRes.data.items[0];
    if (!post) {
      throw new Error('Страница с нужным адресом не была найдена');
    }

    const getPostDataRes = await $api.infoPages.getPostData({ id: post.id });
    if (!getPostDataRes.success) {
      throw new Error(getPostDataRes.message);
    }

    // if (!getPostDataRes.data.content && !getPostDataRes.data.structure && !getPostDataRes.data.brands) {
    //   throw new Error('Ошибка при получении содержимого статьи');
    // }

    infoPagesStore.detailPosts[post.id] = {
      ...getPostDataRes.data,
      url: INFO_PAGES_CONFIG.ignoreTrailingSlashInPostsUrls && post.url.length > 1 ? post.url.replace(/\/$/, '') : post.url,
    };

    return {
      posts: getPostsRes.data,
      postsData: getPostDataRes.data,
    };
  };

  /**
   * Получает детальную информацию о посте по ID.
   * Использует запрос `GET v1/posts/{id}/`.
   *
   * @param {Object} payload - Данные для запроса
   * @param {number} payload.id - ID поста
   * @param {string} [payload.fullPath] - Полный путь поста (необязательный)
   * @throws {Error} Если запрос неуспешен или отсутствует контент
   * @returns {Promise<IResponse<IAPIGetPostDataResponse>>} Ответ API с данными поста
   */
  const getDetailPostById = async (payload: { id: number; fullPath?: string }) => {
    const response = await $api.infoPages.getPostData(payload);
    if (!response.success) {
      throw new Error(response.message);
    }

    if (!response.data.content) {
      throw new Error('Ошибка при получении содержимого статьи');
    }

    infoPagesStore.detailPosts[payload.id] = response.data;

    return response;
  };

  return {
    getDetailPostByFullPath,
    getSectionPosts,
    getDetailPostById,
  };
};
